import React, { useEffect, useRef } from "react";
import { Modal, StyleSheet, View, Animated } from "react-native";
import { TModal } from "../../../utils/types";
import {
  OnboardingWelcomeModal,
  OnboardingDeployWidgetModal,
  OnboardingDeployWidgetCompleteModal,
  OnboardingSimulationsModal,
  OnboardingSimulationsCompleteModal,
  OnboardingBeforeAfterModal,
  OnboardingBeforeAfterCompleteModal,
  OnboardingFutureGrowthModal,
  OnboardingConsultModal,
} from "./OnboardingModals";
import { SessionContext } from "../../../contexts";
import { useOnboardingTips } from "../../../contexts/OnboardingTipsContext";

type OnboardingModalOpenerProps = {
  navigate?: (path: string) => void;
};

const OnboardingModalOpener: React.FC<OnboardingModalOpenerProps> = ({
  navigate,
}) => {
  type LocalOnboardingStep =
    | "deployWidget"
    | "simulate"
    | "beforeAfter"
    | "consult"
    | null;

  const sessionContext = React.useContext(SessionContext)!;
  const { onboardingModal, setOnboardingModal } = sessionContext;
  const session = sessionContext.session;
  const { visible, type } = onboardingModal;
  const [waitingForLocalTips, setWaitingForLocalTips] =
    React.useState<LocalOnboardingStep>(null);
  const { tipsState } = useOnboardingTips();
  const isLeadCaptureGoal = session?.primaryGoal === "lead_capture";

  const handleModalClose = () => {
    setOnboardingModal({ visible: false, type });
  };

  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const transitionTo = (
    nextType: TModal,
    nextRoute: string,
    localStepToWaitFor?: LocalOnboardingStep,
  ) => {
    setIsTransitioning(true);
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      if (localStepToWaitFor) {
        setOnboardingModal({ visible: false, type });
        setWaitingForLocalTips(localStepToWaitFor);
        navigate?.(nextRoute);
        setIsTransitioning(false);
        return;
      }

      setOnboardingModal({ visible: false, type: nextType });
      navigate?.(nextRoute);

      setTimeout(() => {
        setOnboardingModal({ visible: true, type: nextType });
        setIsTransitioning(false);
      }, 300);
    });
  };

  useEffect(() => {
    if (visible && !isTransitioning) {
      fadeAnim.setValue(0);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, type, isTransitioning]);

  useEffect(() => {
    if (
      waitingForLocalTips === "deployWidget" &&
      tipsState.deployWidgetTipsCompleted
    ) {
      // Keep waitingForLocalTips so renderModal shows the complete card; the
      // user's next click calls transitionTo with a new localStepToWaitFor,
      // which overwrites it.
      setOnboardingModal({ visible: true, type: "OnboardingDeployWidget" });
    }

    if (waitingForLocalTips === "consult" && tipsState.consultTipsCompleted) {
      setWaitingForLocalTips(null);
      setOnboardingModal({ visible: true, type: "OnboardingSimulations" });
    }

    if (
      waitingForLocalTips === "simulate" &&
      tipsState.simulationTipsCompleted
    ) {
      setWaitingForLocalTips(null);
      setOnboardingModal({
        visible: true,
        type: isLeadCaptureGoal
          ? "OnboardingFutureGrowth"
          : "OnboardingBeforeAfter",
      });
    }

    if (
      waitingForLocalTips === "beforeAfter" &&
      tipsState.beforeAfterTipsCompleted
    ) {
      setWaitingForLocalTips(null);
      setOnboardingModal({ visible: true, type: "OnboardingFutureGrowth" });
    }
  }, [isLeadCaptureGoal, waitingForLocalTips, tipsState]);

  const renderModal = () => {
    // Step 1: Welcome
    if (type === "OnboardingWelcome") {
      return (
        <OnboardingWelcomeModal
          onNext={() => {
            transitionTo("OnboardingDeployWidget", "/home");
          }}
        />
      );
    }

    // Step 2a: Deploy Widget intro (before tooltips)
    if (type === "OnboardingDeployWidget" && waitingForLocalTips === null) {
      return (
        <OnboardingDeployWidgetModal
          onNext={() =>
            transitionTo(
              "OnboardingDeployWidget",
              "/deploy-widget",
              "deployWidget",
            )
          }
        />
      );
    }

    // Step 2b: Deploy Widget complete (after in-page tour finishes)
    if (
      type === "OnboardingDeployWidget" &&
      waitingForLocalTips === "deployWidget" &&
      tipsState.deployWidgetTipsCompleted
    ) {
      const nextLabel = isLeadCaptureGoal
        ? "Next: Engage Patients with Simulations"
        : "Next: Document Your Consults Automatically";
      return (
        <OnboardingDeployWidgetCompleteModal
          nextActionLabel={nextLabel}
          onNext={() => {
            if (isLeadCaptureGoal) {
              transitionTo("OnboardingSimulations", "/simulate", "simulate");
              return;
            }

            transitionTo("OnboardingConsult", "/consults", "consult");
          }}
        />
      );
    }

    // Step 3: Consult intro (before tooltips)
    if (type === "OnboardingConsult" && waitingForLocalTips === null) {
      return (
        <OnboardingConsultModal
          onNext={() =>
            transitionTo("OnboardingConsult", "/consults", "consult")
          }
        />
      );
    }

    // Step 3: Simulations intro
    if (type === "OnboardingSimulations" && waitingForLocalTips === null) {
      return (
        <OnboardingSimulationsModal
          onNext={() =>
            transitionTo("OnboardingSimulations", "/simulate", "simulate")
          }
        />
      );
    }

    // Step 4: Simulations complete
    if (
      type === "OnboardingSimulations" &&
      waitingForLocalTips === "simulate" &&
      tipsState.simulationTipsCompleted
    ) {
      return (
        <OnboardingSimulationsCompleteModal
          onNext={() => {
            if (isLeadCaptureGoal) {
              transitionTo("OnboardingFutureGrowth", "/home");
              return;
            }

            transitionTo(
              "OnboardingBeforeAfter",
              "/before-afters",
              "beforeAfter",
            );
          }}
        />
      );
    }

    // Step 5: Before/After intro
    if (type === "OnboardingBeforeAfter" && waitingForLocalTips === null) {
      return (
        <OnboardingBeforeAfterModal
          onNext={() =>
            transitionTo(
              "OnboardingBeforeAfter",
              "/before-afters",
              "beforeAfter",
            )
          }
        />
      );
    }

    // Step 6: Before/After complete
    if (
      type === "OnboardingBeforeAfter" &&
      waitingForLocalTips === "beforeAfter" &&
      tipsState.beforeAfterTipsCompleted
    ) {
      return (
        <OnboardingBeforeAfterCompleteModal
          onNext={() => transitionTo("OnboardingFutureGrowth", "/home")}
        />
      );
    }

    // Step 7: Final modal
    if (type === "OnboardingFutureGrowth") {
      return <OnboardingFutureGrowthModal onNext={handleModalClose} />;
    }

    return null;
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={handleModalClose}
    >
      <View style={styles.centeredView}>
        <Animated.View style={[styles.animatedModal, { opacity: fadeAnim }]}>
          {!isTransitioning && renderModal()}
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    padding: 16,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  animatedModal: {
    alignSelf: "center",
    width: "100%",
    maxWidth: 500,
  },
});

export default OnboardingModalOpener;
